import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { Form, Modal, ModalProps } from 'antd';
import moment from 'moment';

import { useDefaultFormErrorNotification } from '@/hooks';

import QuotationForm from '../QuotationForm';
import { Quotation } from '@/core/domain/stock/Quotations/Quotations.types';
import { useUpdateQuotation } from '@/core/domain/stock/Quotations/Quotations.hooks';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: () => void;
  quotation?: Quotation;
};

const UpdateQuotationModal = ({ visible, onCancel, quotation }: Props) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  useEffect(() => {
    if (visible) {
      if (quotation) {
        form.setFieldsValue({
          companyId: quotation.company.id,
          employeeId: quotation.employee.id,
          date: moment(quotation.date),
        });
      }
    }
  }, [visible, quotation]);

  const mutation = useUpdateQuotation();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const obj = {
        ...values,
        id: Number(quotationId),
      };

      await mutation.mutateAsync(obj);

      handleCancel();
    } catch (err) {
      sendFormErrorNotification();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'quotations.update' })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={mutation.isLoading}
    >
      <QuotationForm form={form} quotationCompanyId={quotation?.company.id} />
    </Modal>
  );
};

export default UpdateQuotationModal;
